import { loginSchema } from "@caloteiros/shared";
import type { LoginInput } from "@caloteiros/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactElement } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import logo from "../assets/logowtext.svg";
import { Field, Spinner } from "../components/ui";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";

export function LoginPage(): ReactElement {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login(values);
      navigate("/app", { replace: true });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui entrar agora.");
    }
  });

  return (
    <main className="auth-page">
      <Link to="/">
        <img className="auth-logo" src={logo} alt="Caloteiros" />
      </Link>

      <div className="stack">
        <h1>Bem-vindo de volta</h1>
        <p className="muted">Entre para ver quanto o pessoal ainda te deve.</p>
      </div>

      <form className="stack" onSubmit={onSubmit} noValidate>
        <Field label="E-mail" error={errors.email?.message}>
          <input
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            aria-invalid={Boolean(errors.email)}
            {...register("email")}
          />
        </Field>

        <Field label="Senha" error={errors.password?.message}>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            aria-invalid={Boolean(errors.password)}
            {...register("password")}
          />
        </Field>

        <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
          {isSubmitting ? <Spinner /> : "Entrar"}
        </button>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          <GoogleLogin
            onSuccess={async (credentialResponse) => {
              if (credentialResponse.credential) {
                try {
                  await googleLogin(credentialResponse.credential);
                  navigate("/app", { replace: true });
                } catch (error) {
                  toast.error(error instanceof ApiError ? error.message : "Erro ao entrar com Google.");
                }
              }
            }}
            onError={() => {
              toast.error("Erro ao entrar com Google.");
            }}
          />
        </div>
      </form>

      <p className="center muted">
        Ainda não tem conta? <Link to="/criar-conta">Criar agora</Link>
      </p>
    </main>
  );
}
